import SurveyItem from './SurveyItem';
import isNil from 'lodash/isNil';
import { ResponseValueTypes } from './SurveyResponse';

/**
 * Assigns pre order indexing to all nodes and returns the new currentt index
 */
const nodePreOrderIndexingReduction = (
  surveyNodes: SurveyNode[] = [],
  startIndex: number,
): number => {
  return surveyNodes.reduce(
    (currentIndex, node) => node.assignPreOrderIndex(currentIndex),
    startIndex,
  );
};

/**
 * Assigns post order indexing to all nodes and returns the new currentt index
 */
const nodePostOrderIndexingReduction = (
  surveyNodes: SurveyNode[] = [],
  startIndex: number,
): number => {
  return surveyNodes.reduce(
    (currentIndex, node) => node.assignPostOrderIndex(currentIndex),
    startIndex,
  );
};

/**
 * A node in the survey tree
 *
 * @class SurveyNode
 */
class SurveyNode {
  _parentNode: SurveyNode;
  _surveyItem: SurveyItem;
  _childNodes: SurveyNode[];

  /**
   * Index of node in pre-order survey tree traversal
   */
  private _preOrderIndex: number;

  /**
   * Index of node in post-order survey tree traversal
   */
  private _postOrderIndex: number;

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  constructor(surveyItem: SurveyItem, parentNode: SurveyNode = null) {
    if (!surveyItem.isRootItem && !parentNode) {
      throw new Error(
        'Non-root survey items must provide a parent link for upward traversal',
      );
    }

    this._parentNode = parentNode;
    this._surveyItem = surveyItem;
    this._childNodes = surveyItem.hasSubItems
      ? surveyItem.subItems.map(subItem => new SurveyNode(subItem, this))
      : [];
  }

  assignPreOrderIndex(currentTreeIndex: number): number {
    // assign index to self then to children
    this._preOrderIndex = currentTreeIndex;

    if (this.isLeafNode) {
      return currentTreeIndex + 1;
    } else {
      return nodePreOrderIndexingReduction(
        this.childNodes,
        currentTreeIndex + 1,
      );
    }
  }

  assignPostOrderIndex(currentTreeIndex: number): number {
    // assign index to children then to self
    const indexAfterChildren = this.isLeafNode
      ? currentTreeIndex
      : nodePostOrderIndexingReduction(this.childNodes, currentTreeIndex);

    this._postOrderIndex = indexAfterChildren;

    return this._postOrderIndex + 1;
  }

  get preOrderIndex() {
    if (isNil(this._preOrderIndex)) {
      console.error(
        'Attempted to retrieve pre order index of node but index has not been assigned.  This should be initialized with survey tree.',
      );
    }

    return this._preOrderIndex;
  }

  get postOrderIndex() {
    if (isNil(this._postOrderIndex)) {
      console.error(
        'Attempted to retrieve post order index of node but index has not been assigned.  This should be initialized with survey tree.',
      );
    }

    return this._postOrderIndex;
  }

  /**
   * The parent node.  Allows for up-tree lookup
   *
   * @readonly
   * @type {SurveyNode}
   * @memberof SurveyNode
   */
  get parentNode(): SurveyNode {
    return this._parentNode;
  }

  get isRootNode(): boolean {
    return this._parentNode === null;
  }

  get surveyItem(): SurveyItem {
    return this._surveyItem;
  }

  /**
   * The unique identifier for the node
   *
   * @readonly
   * @type {string}
   * @memberof SurveyNode
   */
  get nodeLink(): string {
    return this._surveyItem.link;
  }

  /**
   * Is this survey item a leaf node on the survey tree
   *
   * @readonly
   * @type {Boolean}
   * @memberof SurveyNode
   */
  get isLeafNode(): boolean {
    return this._childNodes.length === 0;
  }

  get childNodes(): SurveyNode[] {
    return this._childNodes;
  }
}

/**
 * A survey is represented in a tree like structure.  Traversals and lookups will be common operations
 * throughout the taking of a survey.  This object maps survey and provides effecient lookup operations
 *
 * @export
 * @class SurveyTree
 */
export default class SurveyTree {
  _surveyTrees: SurveyNode[];
  _nodeHashMap: { [linkId: string]: SurveyNode };
  _patientInfo: { [key: string]: string };

  constructor(rootSurveyItems: SurveyItem[]) {
    // a survey can have more than one "root node" since there is an array of root items
    this._surveyTrees = rootSurveyItems.map(
      rootSurveyItem => new SurveyNode(rootSurveyItem),
    );

    // hashmap from node link to node for constant time tree lookup
    this._nodeHashMap = {};
    const traverseAndAddToHashMap = (surveyNode: SurveyNode) => {
      this._nodeHashMap[surveyNode.nodeLink] = surveyNode;
      surveyNode.childNodes.forEach(childNode =>
        traverseAndAddToHashMap(childNode),
      );
    };

    // add each root node's tree nodes to hash map
    this._surveyTrees.forEach(rootNode => traverseAndAddToHashMap(rootNode));

    // assign indexing
    nodePreOrderIndexingReduction(this._surveyTrees, 0);
    nodePostOrderIndexingReduction(this._surveyTrees, 0);
  }

  /**
   * Retrieves the survey item with the link
   *
   * @param {string} link
   * @returns {SurveyNode}
   * @memberof SurveyTree
   */
  getSurveyItemWithLink(link: string): SurveyItem {
    const targetNode = this._nodeHashMap[link];

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetNode) return null;

    return targetNode.surveyItem;
  }

  /**
   * Retrieves the survey tree pre order index of target link
   *
   * @param {string} link
   * @returns {SurveyNode}
   * @memberof SurveyTree
   */
  getPreOrderIndexOfLink(link: string): number {
    const targetNode = this._nodeHashMap[link];

    if (!targetNode) {
      console.error(
        'Attempted to retrieve pre order index of a non-existent node.',
      );
      return -1;
    }

    return targetNode.preOrderIndex;
  }

  /**
   * Retrieves the survey tree post order index of target link
   *
   * @param {string} link
   * @returns {SurveyNode}
   * @memberof SurveyTree
   */
  getPostOrderIndexOfLink(link: string): number {
    const targetNode = this._nodeHashMap[link];

    if (!targetNode) {
      console.error(
        'Attempted to retrieve pre order index of a non-existent node.',
      );
      return -1;
    }

    return targetNode.postOrderIndex;
  }

  /**
   * Retrieves the link path to the menu item
   *
   * @param {string} link
   * @returns {string[]}
   * @memberof SurveyTree
   */
  getPathOfItem(link: string): string[] {
    const targetNode = this._nodeHashMap[link];

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetNode) return null;

    const path = [targetNode.nodeLink];

    if (targetNode.isRootNode) return path;

    let parentNode = targetNode.parentNode;
    while (!parentNode.isRootNode) {
      path.push(parentNode.nodeLink);
      parentNode = parentNode.parentNode;
    }

    path.push(parentNode.nodeLink);
    // switch path from target node -> root to root -> target node
    path.reverse();
    return path;
  }

  /**
   * Retrieves all links in the survey
   */
  get allLinks(): string[] {
    return Object.keys(this._nodeHashMap);
  }

  /**
   * Retrieves all survey items in the survey
   */
  get allItems(): SurveyItem[] {
    return this.allLinks.map(link => this.getSurveyItemWithLink(link));
  }

  /**
   * Retrieves the links of controls below the root link
   *
   * @param {string} rootLink - optional.  Only required if paginated form
   * @type {string[]}
   * @memberof SurveyTree
   */
  getSubLeafLinks(rootLink: string): string[] {
    return this.allLinks
      .map(link => this._nodeHashMap[link])
      .filter((surveyNode: SurveyNode) => surveyNode.isLeafNode)
      .filter((surveyNode: SurveyNode) => {
        // ensure it is below the root link
        if (!rootLink) return true;

        const path = this.getPathOfItem(surveyNode.nodeLink);
        return path[0] === rootLink;
      })
      .map(node => node.nodeLink);
  }

  _getResponseValueType(type: string): ResponseValueTypes {
    let responseValueType: ResponseValueTypes = 'valueString';
    switch (type) {
      case 'string':
        responseValueType = 'valueString';
        break;
      case 'date':
        responseValueType = 'valueDate';
        break;
    }
    return responseValueType;
  }

  /**
   * Retrieves all the sub links of the target link
   *
   * @param {string} rootLink - optional.  Only required if paginated form
   * @type {string[]}
   * @memberof SurveyTree
   */
  getSubLinks(targetLink: string): string[] {
    const targetNode = this._nodeHashMap[targetLink];
    if (
      !targetNode ||
      !targetNode.childNodes ||
      !targetNode.childNodes.length
    ) {
      return [];
    }

    return this.allLinks
      .map(link => this._nodeHashMap[link])
      .filter((surveyNode: SurveyNode) => {
        const path = this.getPathOfItem(surveyNode.nodeLink);
        return surveyNode.nodeLink !== targetLink && path.includes(targetLink);
      })
      .map(node => node.nodeLink);
  }

  /**
   * Returns the sibling link along with the target link in an array
   * @param targetLink
   */
  getSiblingLinks(targetLink: string): string[] {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to retrieve sibling links of a non-existent link of: "${targetLink}"`,
      );
      return [];
    }

    if (targetNode.isRootNode) {
      // a root node's sibling are other root nodes
      return this._surveyTrees.map(tree => tree.nodeLink);
    } else {
      // the parent's children are all sibling nodes
      const parentNode = targetNode.parentNode;

      if (!parentNode) {
        // this should never happen
        console.error(
          `Unable to find parent node for non-root node with link: "${targetLink}".  This indicates an incorrectly constructed parent tree`,
        );
        return [];
      }
      return parentNode.childNodes.map(siblingNode => siblingNode.nodeLink);
    }
  }

  /**
   * Determines if the given node link is the first among its sibling nodes
   */
  getIsFirstSibling(targetLink: string): boolean {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to check sibling node relation of a non-existent link of: "${targetLink}"`,
      );
      return false;
    }

    const siblings = targetNode.isRootNode
      ? this._surveyTrees
      : targetNode.parentNode.childNodes;

    return siblings.findIndex(node => node.nodeLink === targetLink) === 0;
  }

  /**
   * Determines if the given node link is the last among its sibling nodes
   */
  getIsLastSibling(targetLink: string): boolean {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to check sibling node relation of a non-existent link of: "${targetLink}"`,
      );
      return false;
    }

    const siblings = targetNode.isRootNode
      ? this._surveyTrees
      : targetNode.parentNode.childNodes;

    return (
      siblings.findIndex(node => node.nodeLink === targetLink) ===
      siblings.length - 1
    );
  }

  /**
   * Determines if the given node has no siblings
   */
  getHasNoSibligs(targetLink: string): boolean {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to check sibling node relation of a non-existent link of: "${targetLink}"`,
      );
      return false;
    }

    const siblings = targetNode.isRootNode
      ? this._surveyTrees
      : targetNode.parentNode.childNodes;

    return siblings.length <= 1;
  }

  /**
   * Return the parent link of the target survey item
   * @param targetLink
   */
  getParentLink(targetLink: string): string {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to retrieve the parent link of a non-existent link of: "${targetLink}"`,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    if (targetNode.isRootNode) {
      // a root node won't have a parent
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    } else {
      const parentNode = targetNode.parentNode;
      if (!parentNode) {
        console.error(
          `A non-root node with link "${targetLink}" did not have a parent.  This indicates an incorrectly structured tree.`,
        );
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return null;
      }
      return parentNode.nodeLink;
    }
  }

  /**
   * Return the links of the direct children of the target link
   * @param targetLink
   */
  getDirectChildrenLinks(targetLink: string): string[] {
    const targetNode = this._nodeHashMap[targetLink];

    if (!targetNode) {
      console.error(
        `Attempted to retrieve the children links of a non-existent link of: "${targetLink}"`,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    return targetNode.childNodes.map(childNode => childNode.nodeLink);
  }

  /**
   * The root/page survey items of this survey
   */
  rootSurveyItems(): SurveyItem[] {
    return this._surveyTrees.map(tree => tree.surveyItem);
  }
}
